import {html, LitElement, PropertyValues} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {translate} from '@stefanholzapfel/lit-translate';
import {translateUppercase} from '@stefanholzapfel/lit-translate/dist/translateUppercase.directive';
import {styles} from './sp-statistic-control.styles';
import {SpHierarchicalSelectOption} from '../../inputs/sp-hierarchical-select/sp-hierarchical-select';
import {getIcon} from '../../../styles/icons';
import {when} from 'lit/directives/when.js';

@customElement('sp-statistic-control')
export class SpStatisticControl extends LitElement {
    static styles = [
        styles
    ];
    @property()
    period: SpStatisticPeriod;
    @property({type: Array})
    fields: SpStatisticField[];
    @property()
    field: SpStatisticField;
    @property()
    fieldSelectEnabled: Boolean;
    @state()
    title: string;
    @state()
    fieldSelectOptions: SpHierarchicalSelectOption[] = [];

    legacyButtons = false;

    protected firstUpdated = () => {

    }

    protected willUpdate(changedProperties: PropertyValues<this>) {
        if (changedProperties.has('period')) {
            this.setPeriod(this.period.type);
        }
        if (changedProperties.has('fields') && this.fields) {
            this.fieldSelectOptions = this.fields.map(field => {
                return {
                    id: field.name,
                    label: translate(`CONFIG_FIELD_${field.id}_LABEL`)
                };
            });
        }
        if (changedProperties.has('field') && this.field) {
            this.dispatchFieldUpdate();
        }
    }

    render() {
        return html`
            ${when(this.period, () => html`
                <div class="time-nav">
                    <a class="left ${this.period.type == 'total' ? 'disabled' : ''}" @click=${() => {
                        this.setPeriod(this.period.type, -1)
                    }}></a>
                    <div class="title">
                        ${this.period.type == 'week' ? translate('STATISTICS_PERIOD_WEEK') : ''}
                        ${this.period.type == 'total' ? translate('STATISTICS_PERIOD_TOTAL') : ''}
                        ${this.title}
                    </div>
                    <a class="right ${this.period.end < new Date() ? '' : 'disabled'}" @click=${() => {
                        this.setPeriod(this.period.type, 1)
                    }}></a>
                </div>
                ${when(this.fields.length > 1, () => html`
                    <div class="buttons">
                        <sp-hierarchical-select
                                id="period-select"
                                .dialogHeader=${translate(`STATISTICS_PERIOD_SELECT`)}
                                .options=${[
                                    {id: 'week', 'label': translate('STATISTICS_PERIOD_WEEK')},
                                    {id: 'month', 'label': translate('STATISTICS_PERIOD_MONTH')},
                                    {id: 'year', 'label': translate('STATISTICS_PERIOD_YEAR')},
                                    {id: 'total', 'label': translate('STATISTICS_PERIOD_TOTAL')},
                                ]}
                                .value=${this.period.type}
                                required=true
                                .dialogButtonLabels=${
                                        {
                                            ok: translateUppercase('MODAL_OK'),
                                            cancel: translateUppercase('MODAL_CANCEL'),
                                            back: translateUppercase('MODAL_BACK')
                                        }
                                }
                                @change=${(e) => {
                                    this.setPeriod(e.detail.value, 0);
                                }}>
                        </sp-hierarchical-select>
                        <sp-hierarchical-select
                                id="field-select"
                                .dialogHeader=${translate(`STATISTICS_FIELD_SELECT`)}
                                .options=${this.fieldSelectOptions}
                                .value=${this.field.name}
                                required=true
                                .disabled=${!this.fieldSelectEnabled}
                                .dialogButtonLabels=${
                                        {
                                            ok: translateUppercase('MODAL_OK'),
                                            cancel: translateUppercase('MODAL_CANCEL'),
                                            back: translateUppercase('MODAL_BACK')
                                        }
                                }
                                @change=${(e) => {
                                    let name = e.detail.value;
                                    if (!this.field || this.field.name != name) {
                                        let fields = this.fields.filter((f) => {
                                            return f.name == name;
                                        });
                                        if (fields.length == 1) {
                                            this.field = fields[0];
                                            this.dispatchFieldUpdate();
                                        }
                                    }
                                }}>
                        </sp-hierarchical-select>
                        ${when(false, () => html`
                        <sp-button
                                class="filter-button"
                                @click=${() => this.openFilter()}
                                .icon="url(${getIcon('filter', {color: 'white', size: 'x128'})})"></sp-button>
                        `)}
                    </div>
                `)}
                ${when(this.fields.length <= 1, () => html`
                    <ul class="time-buttons">
                        <li>
                            <a class="${this.period.type == 'week' ? 'selected' : ''}" @click=${() => {
                                this.setPeriod('week', 0)
                            }}>
                                ${translate('STATISTICS_PERIOD_WEEK')}
                            </a>
                        </li>
                        <li>
                            <a class="${this.period.type == 'month' ? 'selected' : ''}" @click=${() => {
                                this.setPeriod('month', 0)
                            }}>
                                ${translate('STATISTICS_PERIOD_MONTH')}
                            </a>
                        </li>
                        <li>
                            <a class="${this.period.type == 'year' ? 'selected' : ''}" @click=${() => {
                                this.setPeriod('year', 0)
                            }}>
                                ${translate('STATISTICS_PERIOD_YEAR')}
                            </a>
                        </li>
                        <li>
                            <a class="${this.period.type == 'total' ? 'selected' : ''}" @click=${() => {
                                this.setPeriod('total', 0)
                            }}>
                                ${translate('STATISTICS_PERIOD_TOTAL')}
                            </a>
                        </li>
                    </ul>
                `)}
            `)}
        `;
    }

    private dispatchFieldUpdate() {
        this.dispatchEvent(new CustomEvent<SpStatisticFieldUpdatedEventPayload>('statistic-field-updated', {
            bubbles: true,
            cancelable: true,
            composed: true,
            detail: {field: this.field}
        }));
    }

    private openFilter() {

    }

    private setPeriod(type: "week" | "month" | "year" | "total", step: -1 | 0 | 1 = 0) {

        const now = new Date();

        if (step == 1 && this.period.end > new Date()) return;

        let nP: SpStatisticPeriod = {
            type: type,
            start: new Date(this.period && type == this.period.type ? this.period.start : now),
            end: new Date(this.period && type == this.period.type ? this.period.end : now)
        };

        this.title = '';

        switch (type) {
            case 'total':
                nP.end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
                nP.start = new Date(nP.end);
                nP.start.setUTCFullYear(nP.start.getUTCFullYear() - 2);
                break;
            case 'week':
                nP.start = new Date(Date.UTC(nP.start.getUTCFullYear(), nP.start.getUTCMonth(), nP.start.getUTCDate() - (nP.start.getUTCDay() + 6) % 7));
                nP.start.setUTCDate(nP.start.getUTCDate() + 7 * step);
                nP.end = new Date(nP.start);
                nP.end.setUTCDate(nP.start.getUTCDate() + 7);
                let weekNumber = this.getWeekNumber(nP.start)
                this.title = `${weekNumber} ${weekNumber == 1 ? nP.end.getUTCFullYear() : nP.start.getUTCFullYear()}`;
                break;
            case 'month':
                nP.start = new Date(Date.UTC(nP.start.getUTCFullYear(), nP.start.getUTCMonth() + step, 1));
                nP.end = new Date(Date.UTC(nP.start.getUTCFullYear(), nP.start.getUTCMonth() + 1, 0, 23, 59, 59));
                this.title = `${('' + (nP.start.getUTCMonth() + 1)).padStart(2, '0')} ${nP.start.getFullYear()}`;
                break;
            case 'year':
                nP.start = new Date(Date.UTC(nP.start.getUTCFullYear() + step, 0, 1));
                nP.end = new Date(nP.start);
                nP.end.setUTCFullYear(nP.end.getUTCFullYear() + 1);
                this.title = `${nP.start.getUTCFullYear()}`;
                break;
        }

        if (JSON.stringify(this.period) != JSON.stringify(nP)) {
            this.period.type = nP.type;
            this.period.start = nP.start;
            this.period.end = nP.end;
            this.dispatchEvent(new CustomEvent<SpStatisticPeriodUpdatedEventPayload>('statistic-period-updated', {
                bubbles: true,
                cancelable: true,
                composed: true,
                detail: {period: this.period}
            }));
        }
    }

    private getWeekNumber(d: Date) {
        // Copy date so don't modify original
        const date = new Date(d);
        // Set to nearest Thursday: current date + 4 - current day number
        // Make Sunday's day number 7
        date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
        // Get first day of year
        const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
        // Calculate full weeks to nearest Thursday
        return Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
        // Return array of year and week number
        // return [date.getUTCFullYear(), weekNo];
    }
}

export interface SpStatisticPeriodUpdatedEventPayload {
    period: SpStatisticPeriod
}

export interface SpStatisticFieldUpdatedEventPayload {
    field: SpStatisticField
}

export interface SpStatisticField {
    id: number,
    typeId: number,
    name: string,
    statisticConfig: SpStatisticFieldStatisticConfig,
    options: SpHierarchicalSelectOption[]
}

export interface SpStatisticFieldStatisticConfig {
    integrations: string[];
    aggregationType: string;
}

export interface SpStatisticPeriod {
    type: "week" | "month" | "year" | "total",
    start: Date,
    end: Date
}